import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

const endpoint = `${masterProductFamiliesEndpoint}/sheet/upload`;
export const uploadMasterProductFamiliesSheetEndpointIdentifier = endpoint;

export interface UploadMasterProductFamiliesSheetResultDto {
  created: number;
  updated: number;
}

export interface UploadMasterProductFamiliesSheetErrorDto {
  statusCode?: number;
  message: string;
  errors?: string[];
}

type TData = { data: UploadMasterProductFamiliesSheetResultDto };
type TError = AxiosError<UploadMasterProductFamiliesSheetErrorDto>;
type TVariables = { body: FormData };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Upload a filled master-product-families Excel sheet via POST .../sheet/upload
 * (multipart, field `file`). Name is the only required column; rows whose name
 * matches an existing family update it, others create new families. All-or-nothing:
 * a 400 carries per-row messages in `errors` and nothing is written.
 */
export function useUploadMasterProductFamiliesSheetApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(endpoint, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: (data: TData) => {
      queryClient.invalidateQueries([masterProductFamiliesEndpoint]);
      const { created, updated } = data.data;
      toast({ description: `Uploaded: ${created} created, ${updated} updated`, status: 'success' });
    },
    ...options,
  });
}
